import React, { useState, useRef } from 'react';
import { useActivityData } from './hooks/useActivityData';
import ActivityIconButton from './components/ActivityIconButton';
import ActivityDropdown from './components/ActivityDropdown';
import { ReactComponent as ApprovalIcon } from '../../shared/icons/internal-approvals-icon.svg';
import { ReactComponent as InviteIcon } from '../../shared/icons/invite.svg';
import { ReactComponent as ViewIcon } from '../../shared/icons/view.svg';
import { ReactComponent as SignatureIcon } from '../../shared/icons/signature-with-pen.svg';
import { ReactComponent as ESignatureIcon } from '../../shared/icons/e-signatures-icon.svg';
import styles from './ActivityDashboard.module.scss';

export type ActivityType = 'approvals' | 'invites' | 'review' | 'signing' | 'out_for_signing';

interface ActivityDashboardProps {
  teamId: string;
  onOpenPendingInvitesModal?: () => void;
}

const ActivityDashboard: React.FC<ActivityDashboardProps> = ({ teamId, onOpenPendingInvitesModal }) => {
  const [activeDropdown, setActiveDropdown] = useState<ActivityType | null>(null);
  
  // Create refs for each button
  const approvalsButtonRef = useRef<HTMLButtonElement>(null);
  const invitesButtonRef = useRef<HTMLButtonElement>(null);
  const reviewButtonRef = useRef<HTMLButtonElement>(null);
  const signingButtonRef = useRef<HTMLButtonElement>(null);
  const outForSigningButtonRef = useRef<HTMLButtonElement>(null);
  
  const {
    pendingApprovals,
    dealInvitations,
    documentsInReview,
    documentsInSigningPrep,
    documentsOutForSigning,
    loading,
    loadDetails,
    counts,
    detailsLoaded,
  } = useActivityData(teamId);

  const handleDropdownToggle = (type: ActivityType) => {
    if (activeDropdown === type) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(type);
      loadDetails(); // Load detail data for dropdown display
    }
  };

  const handleCloseDropdown = () => {
    setActiveDropdown(null);
  };

  // Check if there's any activity based on counts
  const hasAnyActivity =
    counts.pendingApprovalsCount > 0 ||
    counts.dealInvitationsCount > 0 ||
    counts.documentsInReviewCount > 0 ||
    counts.documentsInSigningPrepCount > 0 ||
    counts.documentsOutForSigningCount > 0;

  // Show component if loading or has activity
  // This ensures icons appear immediately while data is loading
  if (!hasAnyActivity && !loading) {
    return null;
  }

  return (
    <>
      {(hasAnyActivity || loading) && <div className={styles.divider} />}
      <div className={styles.activityDashboard}>
        {counts.pendingApprovalsCount > 0 && (
          <div className={styles.buttonWrapper}>
            <ActivityIconButton
              ref={approvalsButtonRef}
              icon={<ApprovalIcon />}
              count={counts.pendingApprovalsCount}
              type="approvals"
              tooltip="Pending Approvals"
              onClick={() => handleDropdownToggle('approvals')}
              isActive={activeDropdown === 'approvals'}
            />
            {activeDropdown === 'approvals' && (
              <ActivityDropdown
                type="approvals"
                items={pendingApprovals}
                onClose={handleCloseDropdown}
                loading={!detailsLoaded}
                teamId={teamId}
                totalCount={counts.pendingApprovalsCount}
                buttonRef={approvalsButtonRef}
              />
            )}
          </div>
        )}

        {counts.dealInvitationsCount > 0 && (
          <div className={styles.buttonWrapper}>
            <ActivityIconButton
              ref={invitesButtonRef}
              icon={<InviteIcon />}
              count={counts.dealInvitationsCount}
              type="invites"
              tooltip="Deal Invitations"
              onClick={() => handleDropdownToggle('invites')}
              isActive={activeDropdown === 'invites'}
            />
            {activeDropdown === 'invites' && (
              <ActivityDropdown
                type="invites"
                items={dealInvitations}
                onClose={handleCloseDropdown}
                onOpenPendingInvitesModal={onOpenPendingInvitesModal}
                loading={!detailsLoaded}
                teamId={teamId}
                totalCount={counts.dealInvitationsCount}
                buttonRef={invitesButtonRef}
              />
            )}
          </div>
        )}

        {counts.documentsInReviewCount > 0 && (
          <div className={styles.buttonWrapper}>
            <ActivityIconButton
              ref={reviewButtonRef}
              icon={<ViewIcon />}
              count={counts.documentsInReviewCount}
              type="review"
              tooltip="Documents in Review"
              onClick={() => handleDropdownToggle('review')}
              isActive={activeDropdown === 'review'}
            />
            {activeDropdown === 'review' && (
              <ActivityDropdown
                type="review"
                items={documentsInReview}
                onClose={handleCloseDropdown}
                loading={!detailsLoaded}
                teamId={teamId}
                totalCount={counts.documentsInReviewCount}
                buttonRef={reviewButtonRef}
              />
            )}
          </div>
        )}

        {counts.documentsInSigningPrepCount > 0 && (
          <div className={styles.buttonWrapper}>
            <ActivityIconButton
              ref={signingButtonRef}
              icon={<SignatureIcon />}
              count={counts.documentsInSigningPrepCount}
              type="signing"
              tooltip="Signing Preparation"
              onClick={() => handleDropdownToggle('signing')}
              isActive={activeDropdown === 'signing'}
            />
            {activeDropdown === 'signing' && (
              <ActivityDropdown
                type="signing"
                items={documentsInSigningPrep}
                onClose={handleCloseDropdown}
                loading={!detailsLoaded}
                teamId={teamId}
                totalCount={counts.documentsInSigningPrepCount}
                buttonRef={signingButtonRef}
              />
            )}
          </div>
        )}

        {counts.documentsOutForSigningCount > 0 && (
          <div className={styles.buttonWrapper}>
            <ActivityIconButton
              ref={outForSigningButtonRef}
              icon={<ESignatureIcon />}
              count={counts.documentsOutForSigningCount}
              type="out_for_signing"
              tooltip="Out for Signing"
              onClick={() => handleDropdownToggle('out_for_signing')}
              isActive={activeDropdown === 'out_for_signing'}
            />
            {activeDropdown === 'out_for_signing' && (
              <ActivityDropdown
                type="out_for_signing"
                items={documentsOutForSigning}
                onClose={handleCloseDropdown}
                loading={!detailsLoaded}
                teamId={teamId}
                totalCount={counts.documentsOutForSigningCount}
                buttonRef={outForSigningButtonRef}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ActivityDashboard;
